import { useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  error?: string;
  containerClassName?: string;
}

/**
 * Figma "Checkbox" (116:772): 22px, radius 7, dark fill + white check when on. A real checkbox sits
 * (visually hidden) under the box, so it works with React Hook Form's `register`, forms and keyboards.
 */
export function Checkbox({ label, error, containerClassName, className, id, disabled, ...props }: CheckboxProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={containerClassName}>
      <label
        htmlFor={inputId}
        className={cx(
          'inline-flex items-start gap-(--mbm-space-2-5) text-body-small text-text-secondary',
          disabled ? 'cursor-not-allowed opacity-(--mbm-opacity-disabled)' : 'cursor-pointer',
        )}
      >
        <input
          id={inputId}
          type="checkbox"
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          className={cx('peer sr-only', className)}
          {...props}
        />
        <span
          aria-hidden
          className={cx(
            'flex size-(--mbm-size-checkbox) shrink-0 items-center justify-center rounded-(--mbm-component-radius-checkbox) border-[1.5px] bg-transparent text-text-on-dark',
            error ? 'border-feedback-error' : 'border-border-strong',
            'peer-checked:border-action-primary peer-checked:bg-action-primary',
            'peer-focus-visible:shadow-(--mbm-shadow-focus-ring)',
            '[&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100',
          )}
        >
          <Icon icon={Check} size="sm" />
        </span>
        {label ? <span className="pt-px">{label}</span> : null}
      </label>
      {error ? (
        <p role="alert" className="mt-(--mbm-space-1) text-body-small text-feedback-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
